import { Injectable, signal, effect } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, throwError } from 'rxjs';
import {
  Sala,
  Reserva,
  Espacio,
  CreateEspacioRequest,
  EspacioExtra,
  CreateExtraRequest,
  UpdateExtraRequest,
  CreateExtraResponse,
  CalendarResponse,
  EspacioImage,
  SpacePricingTier,
  CreatePricingTierRequest,
  SpacePricingTiersResponse,
  CalculatedPrice
} from '../models/sala.model';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { finishLoadingAfter } from '../utils/loading';
import { friendlyError } from '../utils/friendly-error';

/** Concepto del catálogo cerrado: cada icono tiene UN significado fijo. */
export interface SpaceFeatureIcon {
  id: string;
  label: string;
  group: string;
  keywords: string;
}

/** Característica de un espacio: general del negocio o personalizada. */
export interface SpaceFeature {
  feature_id: number;
  business_feature_id: number | null;
  kind: 'general' | 'custom';
  name: string;
  description?: string | null;
  icon: string;
  sort_order: number;
  is_active?: boolean;
}

/** Lo que se envía al guardar un espacio: referencia a general o personalizada. */
export type SpaceFeatureInput =
  | { business_feature_id: number }
  | { name: string; description: string; icon: string };

/** Característica general del negocio (reutilizable en sus espacios). */
export interface BusinessFeature {
  business_feature_id: number;
  name: string;
  description?: string | null;
  icon: string;
  sort_order: number;
  is_active: boolean;
  spaces_using: number;
}

export interface BusinessFeatureInput {
  name: string;
  description: string;
  icon: string;
  is_active?: boolean;
  apply_to_all_spaces?: boolean;
}

/** Grupo de colecciones (p. ej. "Salas de reunión"). */
export interface FeatureCollectionGroup {
  group_id: number;
  name: string;
  sort_order: number;
}

/** Colección reutilizable de amenidades incluidas. */
export interface FeatureCollection {
  collection_id: number;
  group_id: number | null;
  name: string;
  description?: string | null;
  sort_order: number;
  icons: string[];
}

export interface FeatureCollectionInput {
  name: string;
  description?: string;
  group_id?: number | null;
  icons: string[];
}

export interface FeatureCollectionsResponse {
  groups: FeatureCollectionGroup[];
  collections: FeatureCollection[];
  max_groups: number;
  max_collections: number;
  max_icons: number;
}

export const MAX_SPACE_FEATURES = 8;

@Injectable({
  providedIn: 'root'
})
export class SalaService {
  private readonly API_URL = `${environment.apiUrl}/space`;
  private readonly ADMIN_LIST_URL = `${this.API_URL}/admin?scope=own`;
  private readonly EXTRAS_API_URL = `${environment.apiUrl}/space-extra`;
  private readonly PRICING_API_URL = `${environment.apiUrl}/space-pricing`;
  private readonly CALENDAR_API_URL = `${environment.apiUrl}/space/calendar/admin`;

  // Señales reactivas para el estado
  public readonly salas = signal<Sala[]>([]);
  public readonly reservas = signal<Reserva[]>([]);
  public readonly espacios = signal<Espacio[]>([]);
  public readonly espaciosLoading = signal<boolean>(false);
  public readonly espaciosInitialized = signal<boolean>(false);
  public readonly espaciosError = signal<string | null>(null);
  public readonly espaciosPage = signal(1);
  public readonly espaciosPages = signal(0);
  public readonly espaciosTotal = signal(0);
  public readonly featuredListingEnabled = signal(false);
  public readonly featuredListingLimit = signal(2);
  public readonly featuredListingSelectedCount = signal(0);
  public readonly espaciosPageSize = 12;

  // undefined = aún no se observó ninguna sesión (evita un reset innecesario
  // en el arranque de la app, cuando el estado ya está vacío).
  private lastBusinessId: number | null | undefined = undefined;

  constructor(private http: HttpClient, private auth: AuthService) {
    // Multi-tenant: esta caché en memoria vive mientras dure la pestaña del
    // navegador (los servicios "providedIn: root" no se destruyen al navegar
    // entre /login y /dashboard). Sin este reset, cambiar de cuenta/negocio en
    // la misma pestaña dejaba visibles los espacios del negocio anterior.
    effect(() => {
      const businessId = this.auth.currentUser()?.id ?? null;
      if (this.lastBusinessId !== undefined && businessId !== this.lastBusinessId) {
        this.reset();
      }
      this.lastBusinessId = businessId;
    });
  }

  /** Limpia el estado en memoria al cambiar de sesión/negocio. */
  reset(): void {
    this.salas.set([]);
    this.reservas.set([]);
    this.espacios.set([]);
    this.espaciosLoading.set(false);
    this.espaciosInitialized.set(false);
    this.espaciosError.set(null);
    this.espaciosPage.set(1);
    this.espaciosPages.set(0);
    this.espaciosTotal.set(0);
    this.featuredListingEnabled.set(false);
    this.featuredListingLimit.set(2);
    this.featuredListingSelectedCount.set(0);
  }

  // ==================== GESTIÓN DE ESPACIOS (API) ====================
  
  /**
   * Listar todos los espacios del usuario
   * El backend devuelve { data: Espacio[], total, limit, offset, hasMore }
   */
  getEspacios(page = 1, search = ''): Observable<{
    data: Espacio[];
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
    featuredListing?: { enabled: boolean; limit: number; selectedCount: number };
  }> {
    const offset = (page - 1) * this.espaciosPageSize;
    return this.http.get<{
      data: Espacio[];
      total: number;
      limit: number;
      offset: number;
      hasMore: boolean;
      featuredListing?: { enabled: boolean; limit: number; selectedCount: number };
    }>(
      `${this.ADMIN_LIST_URL}&limit=${this.espaciosPageSize}&offset=${offset}&search=${encodeURIComponent(search.trim())}`
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Cargar espacios en la señal reactiva
   */
  // `applyMinDelay` solo debe activarse desde una búsqueda/filtro explícita
  // del usuario (botón "Buscar" o Enter) — las recargas automáticas (carga
  // inicial, después de crear/eliminar un espacio, reintentar tras error)
  // deben seguir respondiendo tan rápido como siempre.
  loadEspacios(page = 1, search = '', applyMinDelay = false): void {
    const loadingStartedAt = Date.now();
    this.espaciosLoading.set(true);
    this.espaciosError.set(null);
    const finish = () => applyMinDelay
      ? finishLoadingAfter(loadingStartedAt, () => this.espaciosLoading.set(false))
      : this.espaciosLoading.set(false);
    this.getEspacios(page, search).subscribe({
      next: (response) => {
        this.espacios.set(response.data);
        this.espaciosPage.set(page);
        this.espaciosTotal.set(response.total);
        this.featuredListingEnabled.set(Boolean(response.featuredListing?.enabled));
        this.featuredListingLimit.set(response.featuredListing?.limit || 2);
        this.featuredListingSelectedCount.set(response.featuredListing?.selectedCount || 0);
        this.espaciosPages.set(Math.ceil(response.total / this.espaciosPageSize));
        this.espaciosInitialized.set(true);
        finish();
      },
      error: (error) => {
        console.error('Error al cargar espacios:', error);
        this.espacios.set([]);
        this.espaciosError.set(typeof error === 'string' ? error : 'No fue posible cargar los espacios.');
        this.espaciosInitialized.set(true);
        finish();
      }
    });
  }

  /**
   * Lista simple de espacios para selectores (ej. el selector de "aplica a"
   * del editor de imagen de acceso privado en Mi Sitio) -- a diferencia de
   * loadEspacios(), NO toca las señales paginadas de "Gestión de salas"
   * (evita pisar esa vista si se llama desde otra pantalla) y trae un límite
   * alto en una sola página en vez de paginar de 12 en 12.
   */
  obtenerEspaciosParaSelector(): Observable<Espacio[]> {
    return this.http.get<{ data: Espacio[] }>(`${this.ADMIN_LIST_URL}&limit=200&offset=0`).pipe(
      map(response => response.data),
      catchError(this.handleError)
    );
  }

  /**
   * Obtener un espacio por ID
   */
  getEspacioById(id: number): Observable<Espacio> {
    return this.http.get<Espacio>(`${this.API_URL}/admin/${id}`).pipe(
      catchError(this.handleError)
    );
  }

  getEstadisticasEspacios(businessId: number, from?: string, to?: string): Observable<any> {
    const query = new URLSearchParams();
    query.set('business_id', String(businessId));
    if (from) query.set('from', from);
    if (to) query.set('to', to);
    const suffix = query.toString() ? `?${query.toString()}` : '';
    return this.http.get<any>(`${this.API_URL}/admin/statistics${suffix}`).pipe(catchError(this.handleError));
  }

  /**
   * Crear un nuevo espacio
   */
  crearEspacio(espacio: CreateEspacioRequest): Observable<Espacio> {
    return this.http.post<Espacio>(this.API_URL, espacio).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Actualizar un espacio existente
   */
  actualizarEspacio(id: number, espacio: Partial<CreateEspacioRequest>): Observable<Espacio> {
    return this.http.put<Espacio>(`${this.API_URL}/${id}`, espacio).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Cambiar estado de publicación de un espacio
   */
  toggleEspacioStatus(id: number, is_active: boolean): Observable<Espacio> {
    return this.http.patch<Espacio>(`${this.API_URL}/${id}/status`, { is_active }).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Eliminar un espacio
   */
  eliminarEspacio(id: number, options?: { force: boolean; reason: string }): Observable<any> {
    return this.http.delete<any>(`${this.API_URL}/${id}`, { body: options || {} });
  }

  // ==================== GESTIÓN DE EXTRAS (API) ====================
  
  /**
   * Obtener todos los extras de un espacio
   */
  getExtras(spaceId: number): Observable<EspacioExtra[]> {
    return this.http.get<EspacioExtra[]>(`${this.EXTRAS_API_URL}/admin/space/${spaceId}`).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Crear un nuevo extra para un espacio
   */
  crearExtra(spaceId: number, extra: CreateExtraRequest): Observable<EspacioExtra> {
    return this.http.post<CreateExtraResponse>(`${this.EXTRAS_API_URL}/space/${spaceId}`, extra).pipe(
      map(response => response.extra),
      catchError(this.handleError)
    );
  }

  /**
   * Actualizar un extra existente
   */
  actualizarExtra(extraId: number, extra: UpdateExtraRequest): Observable<EspacioExtra> {
    return this.http.put<CreateExtraResponse>(`${this.EXTRAS_API_URL}/${extraId}`, extra).pipe(
      map(response => response.extra),
      catchError(this.handleError)
    );
  }

  /**
   * Eliminar un extra
   */
  eliminarExtra(extraId: number): Observable<void> {
    return this.http.delete<void>(`${this.EXTRAS_API_URL}/${extraId}`).pipe(
      catchError(this.handleError)
    );
  }

  // ==================== TARIFAS ESCALONADAS (API) ====================

  /**
   * Obtener los niveles de precio (tiers) de un espacio
   */
  getPricingTiers(spaceId: number): Observable<SpacePricingTiersResponse> {
    return this.http.get<SpacePricingTiersResponse>(`${this.PRICING_API_URL}/admin/space/${spaceId}`).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Crear un nivel de precio individual para un espacio
   */
  crearPricingTier(spaceId: number, tier: CreatePricingTierRequest): Observable<SpacePricingTier> {
    return this.http.post<{ msg: string; pricing_tier: SpacePricingTier }>(`${this.PRICING_API_URL}/space/${spaceId}/tier`, tier).pipe(
      map(response => response.pricing_tier),
      catchError(this.handleError)
    );
  }

  /**
   * Eliminar un nivel de precio
   */
  eliminarPricingTier(tierId: number): Observable<void> {
    return this.http.delete<void>(`${this.PRICING_API_URL}/${tierId}`).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Precio real para una cantidad de horas (aplica tarifa escalonada si
   * corresponde) -- usado para la vista previa de precio al crear una
   * reserva manualmente desde el panel. Antes esa vista previa multiplicaba
   * precio_por_hora × horas a mano, sin considerar tarifas escalonadas, así
   * que un negocio con tarifas configuradas veía un estimado distinto al
   * monto real que terminaba cobrándose.
   */
  calcularPrecioPorHoras(spaceId: number, hours: number): Observable<CalculatedPrice> {
    return this.http.get<CalculatedPrice>(`${this.PRICING_API_URL}/space/${spaceId}/calculate?hours=${hours}`).pipe(
      catchError(this.handleError)
    );
  }

  // ==================== GESTIÓN DE IMÁGENES (API) ====================
  
  /**
   * Eliminar una imagen de un espacio
   */
  eliminarImagen(imageId: number): Observable<void> {
    const IMAGES_API_URL = `${environment.apiUrl}/image-space/delteImage`;
    return this.http.delete<void>(`${IMAGES_API_URL}/${imageId}`).pipe(
      catchError(this.handleError)
    );
  }

  destacarImagen(imageId: number): Observable<{ msg: string; image: EspacioImage }> {
    const imagesUrl = `${environment.apiUrl}/image-space/background-image`;
    return this.http.patch<{ msg: string; image: EspacioImage }>(`${imagesUrl}/${imageId}/featured`, {}).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Subir imágenes a un espacio
   */
  subirImagenes(spaceId: number, images: File[]): Observable<any> {
    const IMAGES_API_URL = `${environment.apiUrl}/image-space/upload-background`;
    
    const formData = new FormData();
    formData.append('id_space', spaceId.toString());
    
    // Agregar cada imagen
    images.forEach((image, index) => {
      formData.append('images', image, image.name);
    });

    return this.http.post<any>(IMAGES_API_URL, formData).pipe(
      catchError(this.handleError)
    );
  }

  setFeaturedListing(id: number, is_featured_listing: boolean): Observable<{
    msg: string;
    space: Espacio;
    featuredListing: { enabled: boolean; limit: number; selectedCount: number };
  }> {
    return this.http.patch<{
      msg: string;
      space: Espacio;
      featuredListing: { enabled: boolean; limit: number; selectedCount: number };
    }>(`${this.API_URL}/${id}/featured-listing`, { is_featured_listing }).pipe(
      catchError(this.handleError)
    );
  }

  /** Privatiza/publica UN espacio puntual -- ver Space.is_private. Usa el
   * código de acceso del negocio (Business.is_private/access_code,
   * configurado en "Mi sitio" > "Acceso privado"), sin código propio. */
  setSpacePrivacy(id: number, is_private: boolean): Observable<{ msg: string; space: Espacio }> {
    return this.http.patch<{ msg: string; space: Espacio }>(`${this.API_URL}/${id}/privacy`, { is_private }).pipe(
      catchError(this.handleError)
    );
  }

  // ==================== CALENDARIO (API) ====================
  
  /**
   * Obtener datos del calendario
   */
  getCalendarData(month: number, year: number, includeAll = false): Observable<CalendarResponse> {
    const params = {
      month: month,
      year: year,
      ...(includeAll ? { include_all: 'true' } : {})
    };
    
    return this.http.get<CalendarResponse>(this.CALENDAR_API_URL, { params }).pipe(
      catchError(this.handleError)
    );
  }

  // ==================== CARACTERÍSTICAS INCLUIDAS ====================

  /** Catálogo cerrado de iconos permitidos (lo valida también el backend). */
  getSpaceFeatureIcons(): Observable<SpaceFeatureIcon[]> {
    return this.http.get<{ icons: SpaceFeatureIcon[] }>(`${this.API_URL}/feature-icons`).pipe(
      map(response => response.icons || []),
      catchError(this.handleError)
    );
  }

  getSpaceFeatures(spaceId: number): Observable<SpaceFeature[]> {
    return this.http.get<{ features: SpaceFeature[] }>(`${this.API_URL}/admin/${spaceId}/features`).pipe(
      map(response => response.features || []),
      catchError(this.handleError)
    );
  }

  /** Reemplaza la lista completa; el orden del arreglo es el orden visible. */
  saveSpaceFeatures(spaceId: number, features: SpaceFeatureInput[]): Observable<SpaceFeature[]> {
    return this.http.put<{ features: SpaceFeature[] }>(`${this.API_URL}/admin/${spaceId}/features`, { features }).pipe(
      map(response => response.features || []),
      catchError(this.handleError)
    );
  }

  // Generales del negocio (catálogo propio, reutilizable en todos sus espacios).
  getBusinessFeatures(businessId: number): Observable<BusinessFeature[]> {
    return this.http.get<{ features: BusinessFeature[] }>(`${environment.apiUrl}/business/${businessId}/features`).pipe(
      map(response => response.features || []),
      catchError(this.handleError)
    );
  }

  createBusinessFeature(businessId: number, input: BusinessFeatureInput): Observable<{ feature: BusinessFeature; applied_to_spaces: number; skipped_full: number; skipped_duplicate: number }> {
    return this.http.post<{ feature: BusinessFeature; applied_to_spaces: number; skipped_full: number; skipped_duplicate: number }>(`${environment.apiUrl}/business/${businessId}/features`, input).pipe(
      catchError(this.handleError)
    );
  }

  updateBusinessFeature(businessId: number, featureId: number, input: BusinessFeatureInput): Observable<BusinessFeature> {
    return this.http.put<{ feature: BusinessFeature }>(`${environment.apiUrl}/business/${businessId}/features/${featureId}`, input).pipe(
      map(response => response.feature),
      catchError(this.handleError)
    );
  }

  deleteBusinessFeature(businessId: number, featureId: number): Observable<{ removed_from_spaces: number }> {
    return this.http.delete<{ removed_from_spaces: number }>(`${environment.apiUrl}/business/${businessId}/features/${featureId}`).pipe(
      catchError(this.handleError)
    );
  }

  // Colecciones reutilizables de amenidades (y sus grupos opcionales).
  getFeatureCollections(businessId: number): Observable<FeatureCollectionsResponse> {
    return this.http.get<FeatureCollectionsResponse>(`${environment.apiUrl}/business/${businessId}/feature-collections`).pipe(
      catchError(this.handleError)
    );
  }

  createFeatureCollection(businessId: number, input: FeatureCollectionInput): Observable<{ collection: FeatureCollection }> {
    return this.http.post<{ collection: FeatureCollection }>(`${environment.apiUrl}/business/${businessId}/feature-collections`, input).pipe(
      catchError(this.handleError)
    );
  }

  updateFeatureCollection(businessId: number, collectionId: number, input: FeatureCollectionInput): Observable<{ collection: FeatureCollection }> {
    return this.http.put<{ collection: FeatureCollection }>(`${environment.apiUrl}/business/${businessId}/feature-collections/${collectionId}`, input).pipe(
      catchError(this.handleError)
    );
  }

  deleteFeatureCollection(businessId: number, collectionId: number): Observable<{ msg: string }> {
    return this.http.delete<{ msg: string }>(`${environment.apiUrl}/business/${businessId}/feature-collections/${collectionId}`).pipe(
      catchError(this.handleError)
    );
  }

  createFeatureCollectionGroup(businessId: number, name: string): Observable<{ group: FeatureCollectionGroup }> {
    return this.http.post<{ group: FeatureCollectionGroup }>(`${environment.apiUrl}/business/${businessId}/feature-collections/groups`, { name }).pipe(
      catchError(this.handleError)
    );
  }

  updateFeatureCollectionGroup(businessId: number, groupId: number, name: string): Observable<{ group: FeatureCollectionGroup }> {
    return this.http.put<{ group: FeatureCollectionGroup }>(`${environment.apiUrl}/business/${businessId}/feature-collections/groups/${groupId}`, { name }).pipe(
      catchError(this.handleError)
    );
  }

  deleteFeatureCollectionGroup(businessId: number, groupId: number): Observable<{ msg: string; ungrouped_collections: number }> {
    return this.http.delete<{ msg: string; ungrouped_collections: number }>(`${environment.apiUrl}/business/${businessId}/feature-collections/groups/${groupId}`).pipe(
      catchError(this.handleError)
    );
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    const errorMessage = friendlyError(error, 'Ocurrió un error. Intenta nuevamente.');
    console.error('Error HTTP:', error);
    return throwError(() => errorMessage);
  }

  // Gestión de Salas
  crearSala(sala: Omit<Sala, 'id' | 'fechaCreacion'>): void {
    const nuevaSala: Sala = {
      ...sala,
      id: this.generateId(),
      fechaCreacion: new Date()
    };
    
    this.salas.update(salas => [...salas, nuevaSala]);
  }

  actualizarSala(id: string, sala: Partial<Sala>): void {
    this.salas.update(salas => 
      salas.map(s => s.id === id ? { ...s, ...sala } : s)
    );
  }

  eliminarSala(id: string): void {
    this.salas.update(salas => salas.filter(s => s.id !== id));
    // También eliminar reservas relacionadas
    this.reservas.update(reservas => reservas.filter(r => r.salaId !== id));
  }

  // Gestión de Reservas
  crearReserva(reserva: Omit<Reserva, 'id' | 'fechaCreacion'>): void {
    const nuevaReserva: Reserva = {
      ...reserva,
      id: this.generateId(),
      fechaCreacion: new Date()
    };
    
    this.reservas.update(reservas => [...reservas, nuevaReserva]);
  }

  actualizarReserva(id: string, reserva: Partial<Reserva>): void {
    this.reservas.update(reservas => 
      reservas.map(r => r.id === id ? { ...r, ...reserva } : r)
    );
  }

  eliminarReserva(id: string): void {
    this.reservas.update(reservas => reservas.filter(r => r.id !== id));
  }

  // Obtener reservas por fecha
  getReservasPorFecha(fecha: Date): Reserva[] {
    const fechaStr = fecha.toDateString();
    return this.reservas().filter(r => 
      r.fecha.toDateString() === fechaStr
    );
  }

  // Obtener reservas por sala
  getReservasPorSala(salaId: string): Reserva[] {
    return this.reservas().filter(r => r.salaId === salaId);
  }

  // Verificar disponibilidad
  verificarDisponibilidad(salaId: string, fecha: Date, horaInicio: number, horaFin: number, reservaId?: string): boolean {
    const reservasExistentes = this.getReservasPorFecha(fecha)
      .filter(r => r.salaId === salaId && r.estado !== 'cancelada')
      .filter(r => reservaId ? r.id !== reservaId : true);

    return !reservasExistentes.some(r => 
      (horaInicio >= r.horaInicio && horaInicio < r.horaFin) ||
      (horaFin > r.horaInicio && horaFin <= r.horaFin) ||
      (horaInicio <= r.horaInicio && horaFin >= r.horaFin)
    );
  }

  private generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }
}
